// SafeText - Guaranteed protection against object rendering
// Always returns a string, never an object
import React from 'react';

interface SafeTextProps {
  value: unknown;
  label?: string; // For debugging
  fallback?: string; // Default fallback text
}

export default function SafeText({ value, label, fallback = '' }: SafeTextProps) {
  // Convert to string safely - NEVER return an object
  const safeValue = (() => {
    if (value == null) return fallback;
    
    if (typeof value === "string") return value;
    
    if (typeof value === "number" || typeof value === "boolean") {
      return String(value);
    }
    
    if (typeof value === "object") {
      // Common Firestore object patterns
      const anyV: any = value;
      
      // Try common string properties
      if (typeof anyV.name === "string") return anyV.name;
      if (typeof anyV.title === "string") return anyV.title;
      if (typeof anyV.label === "string") return anyV.label;
      if (typeof anyV.text === "string") return anyV.text;
      if (typeof anyV.value === "string") return anyV.value;
      
      if (import.meta.env.DEV) {
        console.warn("[SafeText] Object detected, attempting extraction", {
          label: label || 'unknown',
          keys: Object.keys(anyV),
          attemptedExtraction: anyV.name || anyV.title || anyV.label || 'none'
        });
      }
      
      // Last resort: try JSON.stringify, but catch errors
      try {
        const jsonStr = JSON.stringify(value);
        // If it's a simple object, return empty string instead of JSON
        if (jsonStr.startsWith('{') && jsonStr.length < 200) {
          return fallback; // Don't render JSON for simple objects
        }
        return jsonStr;
      } catch {
        return fallback;
      }
    }
    
    return fallback;
  })();

  return <React.Fragment>{safeValue}</React.Fragment>;
}

